import React, { FC, memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import { InputTagsProps } from './types';
import { Element } from './Element';

const Component: FC<InputTagsProps> = memo((props) => {
    const { placeholder, values, onTags, name, className, elementClassName } = props;

    const [terms, setTerms] = useState<string[]>(values || []);
    const [value, setValue] = useState('');
    const [focusIndex, setFocusIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const forceInputFocus = () => {
        if (inputRef.current && focusIndex === -1) {
            inputRef.current.focus();
        }
    };

    useLayoutEffect(() => {
        if (terms.length === 0) {
            setFocusIndex(-1);
        }
        onTags({ value: terms, name });
    }, [terms.length]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setTerms(values || []);
    }, [values]);

    useEffect(() => {
        forceInputFocus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusIndex, inputRef.current]);

    const onchange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.currentTarget.value);
    };

    const onkeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = event;
        const currentValue = value.trim();
        if ((key === 'Enter' || key === 'Tab') && currentValue !== '') {
            event.preventDefault();
            event.stopPropagation();
            setTerms([...terms, currentValue.replace(',', '')]);
            setValue('');
            setFocusIndex(-1);
            return;
        }
    };

    const onkeyup = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = event;
        const currentValue = value.trim();
        const valueLength = currentValue.length;
        const currentTarget = event.currentTarget.selectionEnd || 0;
        const isEndOfText = currentTarget > valueLength;
        const isStartOfText = currentTarget === 0;
        const isPossibletermsMove = terms.length > 0;
        const isPossibleAddKeys = key === 'Enter' || key === 'Tab' || key === ',';
        if (isPossibleAddKeys && currentValue !== '') {
            event.preventDefault();
            event.stopPropagation();
            setTerms([...terms, currentValue.replace(',', '')]);
            setValue('');
            setFocusIndex(-1);
            return;
        } else if (isStartOfText && (key === 'Backspace' || key === 'ArrowLeft') && isPossibletermsMove) {
            event.preventDefault();
            setFocusIndex(terms.length - 1);
        } else if (isEndOfText && key === 'ArrowRight' && isPossibletermsMove) {
            event.preventDefault();
            setFocusIndex(0);
        }
    };

    const handleRemove = (index: number, focus: boolean) => {
        setTerms(terms.filter((_, i) => i !== index));
        if (focus) {
            setFocusIndex(Math.max(focusIndex - 1, 0));
        } else {
            forceInputFocus();
        }
    };

    const setSelectedIndex = (index: number) => {
        if (index < terms.length && index > -1) {
            setFocusIndex(index);
        } else {
            setFocusIndex(-1);
        }
    };

    return (
        <div className="form-control h-auto d-inline-flex flex-wrap">
            {terms.map((item, index) => {
                const focus = focusIndex === index;
                return (
                    <Element
                        key={`${item}${index}`}
                        value={item}
                        index={index}
                        onRemove={handleRemove}
                        focus={focus}
                        onSelectedIndex={setSelectedIndex}
                        className={elementClassName}
                    />
                );
            })}
            <input
                data-testid="input-tags"
                ref={inputRef}
                type="text"
                className={classnames('border-0 w-auto flex-fill input-tags', className)}
                placeholder={placeholder}
                aria-label={placeholder}
                value={value}
                onChange={onchange}
                onKeyUp={onkeyup}
                onKeyDown={onkeydown}
                autoFocus
                name={name}
                required={false}
                //{...rest}
            />
        </div>
    );
});

Component.displayName = 'InputTags';
export const InputTags: FC<InputTagsProps> = Object.assign(Component, {});
